/**
 * V101-3-8 (D-CI-38/39): AI resources that AI connections found at the providers
 * this system uses (models, vector stores, agents, guardrails), each with its
 * component role. New ones come first and link to the draft significant change
 * they raised. Renders nothing until a connection has found one; a failed load
 * says so with a retry.
 * V104-D-3 (BL-19): each row names the connection that found it and can be marked
 * "Not used here" (undoable, de-emphasized); a held slot while loading; a bottom
 * "Show fewer"; a focus target after a Retry that finds nothing.
 * API: GET /api/w/:slug/systems/:systemId/ai-resources (any member);
 * PUT/DELETE .../:resourceId/dismissal (owner, admin, editor; the action is hidden from viewers).
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, StatusGlyph } from '@aigovops/design-system';
import { getProvider } from '@aigovops/core';
import { dismissSystemAiResource, getSystemAiResources, restoreSystemAiResource } from '../lib/api';
import type { SystemAiResource } from '../lib/api';
import { queryKeys } from '../lib/query-keys';
import './posture-connections.css';
import './provider-suggestions.css';

const ROLE_LABEL: Record<string, string> = {
  model_ref: 'Model', vector_store: 'Vector store', orchestration: 'Agent', guardrail: 'Guardrail',
  llm_sdk: 'AI SDK', model_file: 'Model file', mcp_server: 'MCP server', eval_tool: 'Evaluation',
};
const CHANNEL_LABEL: Record<string, string> = { bedrock: 'Bedrock', vertex: 'Vertex AI', azure_openai: 'Azure OpenAI', gateway: 'a gateway' };
/** A cloud's own AI service: the maker is the platform, so name the service alone. */
const OWN_CHANNEL: Record<string, { provider: string; label: string }> = {
  bedrock: { provider: 'aws', label: 'Amazon Bedrock' }, vertex: { provider: 'gcp', label: 'Google Vertex AI' }, azure_openai: { provider: 'azure', label: 'Azure OpenAI' },
};
const CAP = 3;

/** "Anthropic via Bedrock" / "Pinecone": the maker, then the route when it is a cloud platform. */
export function providerLabel(key: string | null, channel: string | null): string {
  const own = channel ? OWN_CHANNEL[channel] : undefined;
  if (own && own.provider === key) return own.label;
  const name = (key && getProvider(key)?.name) || key || 'Unknown provider';
  const via = channel ? CHANNEL_LABEL[channel] : undefined;
  return via ? `${name} via ${via}` : name;
}

/** The connection that found a row, in plain words ("your Pinecone connection"). */
export function sourceLabel(key: string | null, channel: string | null): string {
  const own = channel ? OWN_CHANNEL[channel] : undefined;
  if (own) return `your ${own.label} connection`;
  if (channel === 'gateway') return 'your AI Gateway connection';
  return `your ${(key && getProvider(key)?.name) || key || 'AI'} connection`;
}

/** "Oct 3", or "Oct 3, 2025" outside the current year. */
export function shortDate(iso: string, now = new Date()): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}) });
}

export function SystemAiResources({ slug, systemId, canDismiss = false }: { slug: string; systemId: string; canDismiss?: boolean }) {
  const qc = useQueryClient();
  const queryKey = queryKeys.systemAiResources(slug, systemId);
  const q = useQuery({ queryKey, queryFn: () => getSystemAiResources(slug, systemId), enabled: !!slug && !!systemId });
  const [all, setAll] = useState(false);
  const titleId = `ai-resources-${systemId}`;
  const heading = useRef<HTMLHeadingElement>(null);
  // Retry state is local: a refetch with no cached data resets the query to pending,
  // so the error view (and its focused button) stays mounted until the retry settles.
  const [retrying, setRetrying] = useState(false);
  const [tries, setTries] = useState(0);
  const retried = useRef(false);
  // A Retry that succeeds with nothing found keeps the card (and its heading) so focus has a home.
  const [retriedEmpty, setRetriedEmpty] = useState(false);
  // After a successful Retry the error view goes away: put focus on the card heading.
  const [focusHeading, setFocusHeading] = useState(false);
  useEffect(() => {
    if (retried.current && !retrying && q.isSuccess) {
      retried.current = false;
      setRetriedEmpty((q.data?.resources ?? []).length === 0);
      setFocusHeading(true);
    }
  }, [q.isSuccess, q.data, retrying]);
  // Runs after the card for the new state has mounted, so the heading exists.
  useEffect(() => { if (focusHeading && heading.current) { heading.current.focus(); setFocusHeading(false); } }, [focusHeading, retriedEmpty]);
  const retry = async () => {
    if (retrying) return;
    retried.current = true; setRetrying(true); setTries((t) => t + 1);
    try { const r = await q.refetch(); if (!r.isSuccess) retried.current = false; } finally { setRetrying(false); }
  };

  // "Not used here" / Undo: the server owns the state; the list is refetched and focus moves to the row's other control.
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [announce, setAnnounce] = useState('');
  const focusAfter = useRef<{ id: string; control: 'dismiss' | 'undo' } | null>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const toggle = async (r: SystemAiResource, dismiss: boolean) => {
    if (busy) return;
    setBusy(r.resource_id); setFailed(null);
    try {
      await (dismiss ? dismissSystemAiResource(slug, systemId, r.resource_id) : restoreSystemAiResource(slug, systemId, r.resource_id));
    } catch {
      setFailed(`Couldn’t ${dismiss ? 'mark' : 'restore'} ${r.name}. Try again.`);
      setAnnounce('');
      setBusy(null);
      return;
    }
    // The change is saved; a failed refresh must not read as a failed save.
    focusAfter.current = { id: r.resource_id, control: dismiss ? 'undo' : 'dismiss' };
    setAnnounce('');
    await qc.invalidateQueries({ queryKey }).catch(() => undefined);
    setAnnounce(dismiss ? `${r.name} marked not used here.` : `${r.name} is back in use.`);
    setBusy(null);
  };
  useEffect(() => {
    const f = focusAfter.current;
    if (!f || !listRef.current) return;
    const el = [...listRef.current.querySelectorAll<HTMLElement>('[data-resource]')].find((n) => n.dataset.resource === f.id && n.dataset.control === f.control);
    if (el) { focusAfter.current = null; el.focus(); }
    else if (!busy) { focusAfter.current = null; heading.current?.focus(); }
  });

  if ((q.isError && !q.data) || retrying) {
    return <Card className="v101-suggestions-card"><section className="v75-panel v101-suggestions compact v101-ai-resources" aria-labelledby={titleId}>
      <div className="v75-page-heading"><h2 id={titleId} ref={heading} tabIndex={-1}>AI resources at this system’s providers</h2></div>
      <Alert variant="error">{tries > 0 && !retrying ? 'AI resources still couldn’t load.' : 'AI resources couldn’t load.'}{' '}
        <Button variant="ghost" aria-disabled={retrying || undefined} onClick={() => void retry()}>{retrying ? 'Retrying…' : 'Retry'}</Button>
      </Alert>
    </section></Card>;
  }
  // Hold the card's place during the first load so the tabs below do not jump when it lands.
  if (q.isPending) return <div className="v101-ai-resources-slot" aria-hidden="true" />;
  const resources = q.data?.resources ?? [];
  if (resources.length === 0) {
    if (!retriedEmpty) return null;
    return <Card className="v101-suggestions-card"><section className="v75-panel v101-suggestions compact v101-ai-resources" aria-labelledby={titleId}>
      <div className="v75-page-heading"><h2 id={titleId} ref={heading} tabIndex={-1}>AI resources at this system’s providers</h2></div>
      <p className="v75-muted">None found yet. They appear once an AI connection has scanned this system’s providers.</p>
      <Link className="v101-ai-resource-link" to={`/w/${slug}/app/settings/integrations`}>Open connections</Link>
    </section></Card>;
  }
  const shown = all ? resources : resources.slice(0, CAP);
  const toReview = resources.filter((r) => r.change_status === 'planned' && !r.dismissed).length;
  const collapse = () => { setAll(false); heading.current?.focus(); };
  return <Card className="v101-suggestions-card"><section className="v75-panel v101-suggestions compact v101-ai-resources" aria-labelledby={titleId}>
    <div className="v75-page-heading">
      <h2 id={titleId} ref={heading} tabIndex={-1}>AI resources at this system’s providers</h2>
      {resources.length > CAP && <Button variant="ghost" onClick={() => (all ? collapse() : setAll(true))}>{all ? 'Show fewer' : `Show all ${resources.length}${toReview ? ` · ${toReview} to review` : ''}`}</Button>}
    </div>
    <p className="v75-muted">Found in your connected accounts. Not every one is used by this system.</p>
    {failed && <Alert variant="error">{failed}</Alert>}
    <ul className="v101-ai-resources-list" role="list" ref={listRef}>
      {shown.map(r => <li key={r.resource_id} className={r.dismissed ? 'is-dismissed' : undefined}>
        <div className="v101-ai-resource-name">
          <strong>{r.name}</strong>
          <Badge className="v75-status neutral">{ROLE_LABEL[r.ai_role ?? ''] ?? 'AI resource'}</Badge>
          {r.dismissed ? <Badge className="v75-status neutral">Not used here</Badge>
            : r.change_status === 'planned'
            ? <Badge className="v75-status warning"><StatusGlyph kind="warning" /> Change to review</Badge>
            : r.change_id ? <Badge tone="accent">Change in progress</Badge>
            : r.is_new && <Badge tone="accent">New</Badge>}
        </div>
        <span className="v75-muted">{providerLabel(r.provider_key, r.access_channel)}<span aria-hidden="true"> · </span><span className="sr-only">, </span><span className="v101-nowrap">since <time dateTime={r.first_seen_at}>{shortDate(r.first_seen_at)}</time></span></span>
        <span className="v75-muted">Found by {sourceLabel(r.provider_key, r.access_channel)}</span>
        <div className="v101-ai-resource-actions">
          {r.change_id && !r.dismissed && <Link to={`/w/${slug}/app/items/scn/${encodeURIComponent(r.change_id)}`}>{r.change_status === 'planned' ? 'Review change' : 'Open change'}<span className="sr-only"> for {r.name}</span></Link>}
          {canDismiss && (r.dismissed
            ? <Button variant="ghost" data-resource={r.resource_id} data-control="undo" aria-disabled={busy ? true : undefined} onClick={() => void toggle(r, false)}>{busy === r.resource_id ? 'Saving…' : <>Undo<span className="sr-only"> not used here for {r.name}</span></>}</Button>
            : <Button variant="ghost" data-resource={r.resource_id} data-control="dismiss" aria-disabled={busy ? true : undefined} onClick={() => void toggle(r, true)}>{busy === r.resource_id ? 'Saving…' : <>Not used here<span className="sr-only"> for {r.name}</span></>}</Button>)}
        </div>
      </li>)}
    </ul>
    {all && resources.length > CAP && <div className="v101-ai-resources-more"><Button variant="ghost" onClick={collapse}>Show fewer</Button></div>}
    <div className="sr-only" role="status" aria-live="polite">{announce}</div>
  </section></Card>;
}
